import { Injectable } from "@angular/core";
import { FetchStatusSelector } from "@obg/ngrx-utils";

import { CoreState } from "../state";

import { ACTION_PREFIX } from "./auth.action";
import { AuthSelector } from "./auth.selector";
import { CustomerNotVerifiedData, ProfileNotCompletedData, SessionErrorStatus, SessionState } from "./session.model";

export type ɵCoreStateWithSession = Omit<CoreState, "auth"> & { auth: SessionState };

/**
 * WARNING!! TRY TO AVOID USING THIS SELECTOR OUTSIDE OF `ngx.core/auth` FOLDER MUCH AS POSSIBLE!!
 * ACCESS TO `SessionState` WILL NOT BE ANY MORE ALLOWED SOON.
 * If you need to use this state outside of `ngx.core/auth` folder, please first contact core team.
 * What is this: This is state definition required to mantain current session token.
 * All the application logic will be refactored soon not to use SessionState directly
 */
@Injectable({
	providedIn: "root",
})
export class SessionSelector {
	isAuthenticated() {
		return AuthSelector.isAuthenticated;
	}

	getAuthenticationStatus() {
		return AuthSelector.getAuthenticationStatus;
	}

	getSessionToken() {
		return AuthSelector.getSessionToken;
	}

	getAuthenticationToken() {
		return AuthSelector.getAuthenticationToken;
	}

	getTermsAndConditionsToken() {
		return (state: ɵCoreStateWithSession): string | undefined => state.auth.termsAndConditionsToken;
	}

	getTermsAndConditionsResourceKeys() {
		return (state: ɵCoreStateWithSession): string[] | undefined => state.auth.termsAndConditionsResourceKeys;
	}

	getPrivacyPolicyToken() {
		return (state: ɵCoreStateWithSession): string | undefined => state.auth.privacyPolicyToken;
	}

	getEntityLegalNoticeToken() {
		return (state: ɵCoreStateWithSession): string | undefined => state.auth.latestEntityLegalNoticeToken;
	}

	getEntityLegalNoticeBodyKey() {
		return (state: ɵCoreStateWithSession): string | undefined => state.auth.latestEntityLegalNoticeBodyKey;
	}

	getConsentsNotAcceptedToken() {
		return (state: ɵCoreStateWithSession): string | undefined => state.auth.consentsNotAcceptedToken;
	}

	getCustomerNotVerifiedData() {
		return (state: ɵCoreStateWithSession): CustomerNotVerifiedData | undefined =>
			state.auth.customerNotVerifiedData;
	}

	isBusy() {
		return (state: ɵCoreStateWithSession): boolean => FetchStatusSelector.isBusy(ACTION_PREFIX)(state);
	}

	getLastActiveUsername() {
		return (state: ɵCoreStateWithSession): string | undefined =>
			state.auth.lastActiveUsername ? atob(state.auth.lastActiveUsername) : undefined;
	}

	isLastActiveUser() {
		return (state: ɵCoreStateWithSession): boolean | undefined => state.auth.isLastActiveUser;
	}

	getSessionErrorStatus() {
		return (state: ɵCoreStateWithSession): SessionErrorStatus | undefined => state.auth.sessionErrorStatus;
	}

	getSessionErrorStatusMessage() {
		return (state: ɵCoreStateWithSession): string | undefined => state.auth.sessionErrorStatusMessage;
	}

	isSessionExpired() {
		return (state: ɵCoreStateWithSession): boolean => state.auth.sessionErrorStatus === SessionErrorStatus.Expired;
	}

	isSessionInvalid() {
		return (state: ɵCoreStateWithSession): boolean => state.auth.sessionErrorStatus === SessionErrorStatus.Invalid;
	}

	isSessionLimitExpired() {
		return (state: ɵCoreStateWithSession): boolean =>
			state.auth.sessionErrorStatus === SessionErrorStatus.SessionLimitExpired;
	}

	getNextTimeLogin() {
		return (state: ɵCoreStateWithSession): string | undefined => state.auth.nextTimeLogin;
	}

	getIsRedirectBrand() {
		return (state: ɵCoreStateWithSession): boolean | undefined => state.auth.redirectBrand;
	}

	getRemainingAttempts() {
		return (state: ɵCoreStateWithSession): number | undefined => state.auth.remainingAttempts;
	}

	getRememberMe() {
		return (state: ɵCoreStateWithSession): boolean | undefined => state.auth.shouldRememberUser;
	}

	getOneTimeLoginToken() {
		return (state: ɵCoreStateWithSession): string | undefined => state.auth.oneTimeLoginToken;
	}

	getProfileNotCompletedData() {
		return (state: ɵCoreStateWithSession): ProfileNotCompletedData | undefined =>
			state.auth.profileNotCompletedData;
	}

	isAuthenticatedThroughLogin() {
		return (state: ɵCoreStateWithSession): boolean => !!state.auth.authThroughLogin;
	}

	getAccountNotVerifiedTwoFADetails() {
		return (state: ɵCoreStateWithSession): { loginToken: string | undefined; customerId: string | undefined } => ({
			loginToken: state.auth?.loginToken,
			customerId: state.auth?.customerId,
		});
	}
}
